import { type FC, Fragment, type ReactElement, Suspense } from 'react';
import type {
  ActionFunction,
  LoaderFunction,
  RouteObject,
} from 'react-router-dom';
import { createBrowserRouter, Outlet, RouterProvider } from 'react-router-dom';

import {
  markChunkLoadSucceeded,
  reloadOnceForStaleChunk,
} from '@/app/staleChunkReload';

import { generatePreservedRoutes, generateRegularRoutes } from './core';

type Module = {
  default: FC;
  Loader?: LoaderFunction;
  Action?: ActionFunction;
  Catch?: FC;
  Pending?: FC;
};

const PRESERVED = import.meta.glob<Module>('/src/pages/(_app|404).{jsx,tsx}', {
  eager: true,
});

const ROUTES = import.meta.glob<Module>([
  '/src/pages/**/[\\w[-]*.{jsx,tsx,mdx}',
  '!**/(_app|404).*',
]);

const preservedRoutes =
  generatePreservedRoutes<Omit<Module, 'Action'>>(PRESERVED);

const regularRoutes = generateRegularRoutes<
  RouteObject,
  () => Promise<Partial<Module>>
>(ROUTES, (module, key) => {
  const index =
    /index\.(jsx|tsx|mdx)$/.test(key) && !key.includes('pages/index')
      ? { index: true }
      : {};

  return {
    ...index,
    lazy: async () => {
      // Resolved once and reused. Before, `module()` was called four times;
      // Vite returned the same promise, but with failure handling in
      // between that would have been four error paths for the same event.
      let loaded: Partial<Module>;

      try {
        loaded = await module();
        markChunkLoadSucceeded();
      } catch (error) {
        // A missing chunk almost always means a deploy happened while the
        // tab was open. See `staleChunkReload`.
        if (reloadOnceForStaleChunk()) {
          // The reload does not interrupt execution. A promise that never
          // resolves prevents the error screen from painting in the instant
          // left before the browser replaces the document.
          return new Promise<never>(() => {});
        }

        throw error;
      }

      const Element = loaded?.default ?? Fragment;
      const Pending = loaded?.Pending;
      const Page = (): ReactElement =>
        Pending !== undefined ? (
          <Suspense fallback={<Pending />}>
            <Element />
          </Suspense>
        ) : (
          <Element />
        );

      return {
        Component: Page,
        ErrorBoundary: loaded?.Catch,
        loader: loaded?.Loader,
        action: loaded?.Action,
      };
    },
  };
});

const _app = preservedRoutes?._app;
const _404 = preservedRoutes?.['404'];

const Component = _app?.default ?? Fragment;

const App = (): ReactElement =>
  _app?.Pending !== undefined ? (
    <Suspense fallback={<_app.Pending />}>
      <Component />
    </Suspense>
  ) : (
    <Component />
  );

const app = {
  Component: _app?.default !== undefined ? App : Outlet,
  ErrorBoundary: _app?.Catch,
  loader: _app?.Loader,
};

const fallback = {
  path: '*',
  Component: _404?.default ?? Fragment,
};

const routes: RouteObject[] = [
  { ...app, children: [...regularRoutes, fallback] },
];

const Routes = (): ReactElement => (
  <RouterProvider router={createBrowserRouter(routes)} />
);

// eslint-disable-next-line react-refresh/only-export-components
export { Routes, routes };
